import { useAnnouncements } from "../hooks/useApi";

const MESSAGE_PALETTES: Record<string, { bg: string; border: string; text: string; iconBg: string }> = {
  hint: { bg: "bg-status-warning-bg", border: "border-status-warning-text/25", text: "text-status-warning-text", iconBg: "bg-status-warning-text" },
  info: { bg: "bg-status-info-bg", border: "border-status-info-text/25", text: "text-status-info-text", iconBg: "bg-status-info-text" },
  announcement: { bg: "bg-primary-light", border: "border-primary/25", text: "text-primary-dark", iconBg: "bg-primary" },
  warning: { bg: "bg-status-error-bg", border: "border-status-error-text/25", text: "text-status-error-text", iconBg: "bg-status-error-text" },
  success: { bg: "bg-status-success-bg", border: "border-status-success-text/25", text: "text-status-success-text", iconBg: "bg-status-success-text" },
};

const MESSAGE_ICONS: Record<string, React.ReactNode> = {
  hint: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18h6M10 22h4" />
      <path d="M12 2a7 7 0 00-4 12c.8.6 1 1.5 1 2.5V17h6v-.5c0-1 .2-1.9 1-2.5a7 7 0 00-4-12z" />
    </svg>
  ),
  info: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  announcement: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11l18-7v16l-18-7V11z" />
      <path d="M7 15v3a2 2 0 004 0v-2" />
    </svg>
  ),
  warning: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  success: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
};

interface AnnouncementBannerProps {
  placement?: string;
}

export default function AnnouncementBanner({ placement }: AnnouncementBannerProps) {
  const { data: announcements = [] } = useAnnouncements(placement);
  const messages = announcements as Record<string, unknown>[];

  if (!messages.length) return null;

  return (
    <div className="space-y-3">
      {messages.map((m) => {
        const kind = (m.kind as string) || "info";
        const palette = MESSAGE_PALETTES[kind] || MESSAGE_PALETTES.info;
        return (
          <div
            key={m.announcement_id as number}
            className={`flex items-start gap-3 rounded-card border p-4 ${palette.bg} ${palette.border}`}
          >
            <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white ${palette.iconBg}`}>
              {MESSAGE_ICONS[kind] || MESSAGE_ICONS.info}
            </span>
            <div className="min-w-0">
              <p className={`text-h3 font-semibold ${palette.text}`}>{m.title as string}</p>
              <p className="text-body text-text-secondary mt-1 whitespace-pre-line">{m.body as string}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
